<script setup lang="ts">
import { computed } from 'vue'

import type { TmdbImageParagraph } from '../../utils/paragraph-schema/paragraphs'

type SrcSetEntry = { src: string, viewportWidth: number }

const TMDB_BASE_URL = 'https://image.tmdb.org/t/p'
const LAYOUT = { size: 'l' }

const props = withDefaults(defineProps<{
  image: TmdbImageParagraph['image'],
  loading?: 'lazy' | 'eager'
}>(), {
  loading: 'lazy'
})

const width = computed(() => props.image.width ?? 1920)
const height = computed(() => props.image.height ?? 1080)
const isPortrait = computed(() => height.value > width.value)
const src = computed(() => `${TMDB_BASE_URL}/original${props.image.filePath ?? ''}`)

// Possible TMDB poster sizes: w92, w154, w185, w300, w342, w500, w780, original
const srcSet = computed((): SrcSetEntry[] => {
  const filePath = props.image.filePath ?? ''
  return [
    { src: `${TMDB_BASE_URL}/w300${filePath}`, viewportWidth: 360 },
    { src: `${TMDB_BASE_URL}/w342${filePath}`, viewportWidth: 390 },
    { src: `${TMDB_BASE_URL}/w500${filePath}`, viewportWidth: 564 },
    { src: `${TMDB_BASE_URL}/w780${filePath}`, viewportWidth: 999 },
    { src: `${TMDB_BASE_URL}/w500${filePath}`, viewportWidth: 1080 },
    { src: `${TMDB_BASE_URL}/w780${filePath}`, viewportWidth: 1700 },
    { src: `${TMDB_BASE_URL}/original${filePath}`, viewportWidth: 1920 }
  ]
})

const getStyleClassForLayoutSize = (layout: typeof LAYOUT): string => {
  switch (layout?.size) {
    case 'm': return 'bx-content--floating'
    default: return ''
  }
}

const toImageSizeStyleClass = (layout: typeof LAYOUT): string =>
  layout?.size?.length ? 'bx-content--image-' + layout.size : ''
</script>

<template>
  <div :class="['bx-content__media', 'bx-typo--content-media', toImageSizeStyleClass(LAYOUT)]"
       :data-tc="`image-${LAYOUT.size}`">
    <div :class="[getStyleClassForLayoutSize(LAYOUT)]">
      <figure :class="{ 'bx-content__media--portrait': isPortrait }">
        <picture>
          <source v-for="size in srcSet"
                  :key="size.viewportWidth"
                  type="image/jpeg"
                  :media="`(max-width: ${size.viewportWidth}px)`"
                  :srcset="size.src">
          <img :src="src"
               :loading="loading"
               :width="width"
               :height="height"
               title="TMDB Image"
               alt="TMDB Image">
        </picture>
        <figcaption class="bx-content__media-credit bx-content__media-img-credit">
          Foto: TMDB
        </figcaption>
      </figure>
    </div>
  </div>
</template>
